import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { canPerform } from "@vital/shared";
import { useAuth, useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { InspectPanel } from "../components/InspectPanel";
import { MetricCard } from "../components/MetricCard";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import {
  ComposerHeader,
  ComposerPanel,
  ComposerSection,
  ComposerWorkspace,
} from "../components/ComposerChrome";
import {
  OrganizationInspectSummary,
  OrganizationsFilterBar,
  OrganizationsInventoryList,
} from "../components/organizations-inventory";
import { api, ApiError } from "../lib/api";
import { cmsStrings } from "../terminology";
import {
  ORGANIZATIONS_PAGE_NOTE,
  filterAndSortOrganizations,
  type OrganizationListRow,
  type OrganizationSortKey,
} from "../lib/organizations-ops";

const COMPOSER_ID = "org-create-composer";
const ui = cmsStrings.organizationsUi;

export function OrganizationsPage() {
  const { user, refresh, switchOrganization } = useAuth();
  const activeOrg = useActiveOrganization();
  const title = cmsStrings.pages.organizations.title;

  const canCreate = canPerform({
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
    permission: "org.create",
  });

  const [items, setItems] = useState<OrganizationListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [sort, setSort] = useState<OrganizationSortKey>("name");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listOrganizations();
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : ui.loadFailed);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedId && !items.some((r) => r.id === selectedId)) {
      setSelectedId(null);
    }
  }, [items, selectedId]);

  const filtered = useMemo(
    () => filterAndSortOrganizations(items, { query, filter: listFilter, sort }),
    [items, query, listFilter, sort],
  );

  const selected = useMemo(
    () => items.find((r) => r.id === selectedId) ?? null,
    [items, selectedId],
  );

  const activeCount = items.filter((r) => r.status === "active").length;
  const otherCount = items.length - activeCount;

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await api.createOrganization({ code, displayName });
      setCode("");
      setDisplayName("");
      setSuccess(ui.created);
      await loadData();
      await refresh();
      setSelectedId(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : ui.createFailed);
    } finally {
      setSaving(false);
    }
  }

  async function handleSwitch(row: OrganizationListRow) {
    if (row.id === activeOrg.organizationId) return;
    setSwitchingId(row.id);
    setError(null);
    try {
      await switchOrganization(row.id);
      setSuccess(ui.switched);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : ui.switchFailed);
    } finally {
      setSwitchingId(null);
    }
  }

  function renderOrgActions(row: OrganizationListRow): ReactNode {
    const isActive = row.id === activeOrg.organizationId;
    return (
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        disabled={isActive || switchingId === row.id || row.status !== "active"}
        onClick={() => void handleSwitch(row)}
      >
        {isActive ? ui.alreadyActive : ui.switchCta}
      </button>
    );
  }

  if (!canCreate) {
    return (
      <>
        <PageHeader title={title} />
        <p className="banner banner--error" role="alert">
          {ui.noAccess}
        </p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <a href={`#${COMPOSER_ID}`} className="btn btn--primary btn--sm">
            {ui.createCta}
          </a>
        }
      />

      <details className="ops-safety-disclosure" data-vt-organizations-safety>
        <summary>{ui.safetyDisclosure}</summary>
        <p>{ORGANIZATIONS_PAGE_NOTE}</p>
      </details>

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}

      <div className="metric-strip" data-vt-inventory-metrics aria-label={ui.metricsAria}>
        <MetricCard label={ui.metricAll} value={items.length} loading={loading} />
        <MetricCard label={ui.metricActive} value={activeCount} loading={loading} />
        <MetricCard label={ui.metricOther} value={otherCount} loading={loading} />
      </div>

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selected ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selected ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SectionHeader title={ui.listTitle} />
            <OrganizationsFilterBar
              filter={listFilter}
              onFilterChange={setListFilter}
              query={query}
              onQueryChange={setQuery}
              sort={sort}
              onSortChange={setSort}
            />
            {loading ? (
              <InventorySkeleton />
            ) : items.length === 0 ? (
              <OpsEmptyState title={ui.emptyTitle} />
            ) : filtered.length === 0 ? (
              <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
            ) : (
              <OrganizationsInventoryList
                rows={filtered}
                selectedId={selectedId}
                activeOrganizationId={activeOrg.organizationId}
                onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
                renderActions={renderOrgActions}
              />
            )}
          </Card>
          {selected && (
            <InspectPanel
              title={ui.inspectTitle}
              className="content-inspect-panel"
              onClose={() => setSelectedId(null)}
              closeLabel={ui.closeInspect}
              footer={renderOrgActions(selected)}
            >
              <OrganizationInspectSummary
                item={selected}
                isActive={selected.id === activeOrg.organizationId}
              />
            </InspectPanel>
          )}
        </div>

        <ComposerWorkspace id={COMPOSER_ID} layout="solo">
          <ComposerPanel>
            <ComposerHeader
              kind={cmsStrings.composerChrome.kindOrganization}
              title={ui.composerTitle}
              statusLabel={cmsStrings.composerChrome.statusCreate}
              statusTone="info"
            />
            <form
              className="content-form content-form--in-card composer-form"
              onSubmit={(e) => void handleCreate(e)}
            >
              <ComposerSection
                id="org-section-basic"
                title={cmsStrings.composerChrome.sectionBasic}
                bare
              >
                <label>
                  <span>{ui.displayName}</span>
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    disabled={saving}
                  />
                </label>
                <label>
                  <span>{ui.operationalCode}</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    minLength={3}
                    maxLength={64}
                    pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                    autoComplete="off"
                    disabled={saving}
                  />
                </label>
              </ComposerSection>
              <div className="form-actions form-actions--end">
                <button type="submit" className="btn btn--primary" disabled={saving} aria-busy={saving}>
                  {saving ? ui.saving : ui.saveCta}
                </button>
              </div>
            </form>
          </ComposerPanel>
        </ComposerWorkspace>
      </div>
    </>
  );
}
